import { useContext } from "react";
import {
  PermissionsContext,
  defaultPermissionsContextValue,
  type PermissionsContextType,
} from "./permissions-context";

// ============================================
// Hook
// ============================================

export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (!context) {
    if (import.meta.env.DEV) {
      console.warn(
        "[usePermissions] Context unavailable, returning defaults (likely HMR)",
      );
      return defaultPermissionsContextValue;
    }
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
}

/**
 * Convenience hook to check a single permission
 */
export function useHasPermission(permissionKey: string): boolean {
  const { hasPermission, isLoading } = usePermissions();
  return !isLoading && hasPermission(permissionKey);
}

/**
 * Convenience hook to check multiple permissions (any)
 */
export function useHasAnyPermission(permissionKeys: string[]): boolean {
  const { hasAnyPermission, isLoading } = usePermissions();
  return !isLoading && hasAnyPermission(permissionKeys);
}

/**
 * Convenience hook to check multiple permissions (all)
 */
export function useHasAllPermissions(permissionKeys: string[]): boolean {
  const { hasAllPermissions, isLoading } = usePermissions();
  return !isLoading && hasAllPermissions(permissionKeys);
}

/**
 * Convenience hook to check admin status
 */
export function useIsAdminUser(): boolean {
  const { isAdmin, isLoading } = usePermissions();
  return !isLoading && isAdmin();
}

/**
 * Convenience hook to check super admin status
 */
export function useIsSuperAdmin(): boolean {
  const { isSuperAdmin, isLoading } = usePermissions();
  return !isLoading && isSuperAdmin();
}
